import {
    Accounts,
    BaseZenputCollection,
    BaseZenputModel,
    Company,
    Paginator,
    SearchOption,
    User,
    UserRoles
} from './shared/forms';

/**
 * Here we override some of the base models in order to add
 * specific listeners for mobile
 */
export const ActiveUser = User.extend({
    cache_enabled: true,
    cache_key: 'active_user',

    is_logged_in: function () {
        return !!this.id;
    }
});

export const ActiveCompany = Company.extend({
    cache_enabled: true,
    cache_key: 'active_company',

    is_logged_in: function () {
        return !!this.id;
    },

    is_subscription_valid: function () {
        return _.contains(['paying', 'trial'], this.get('status'));
    },

    is_subscription_inactive: function () {
        return this.get('status') === 'inactive';
    }
});

export const MobileAccounts = Accounts.extend({
    cache_enabled: true,
    cache_key: null, // we will set this dynamically in fetch based on this.tag_id
    cache_ttl: 300000, // we don't need to request new accounts more than every 5 min
    cache_fetch_limit: 500, // if cache_merge_on_fetch isn't on (we pass it in as false sometimes)
    cache_merge_on_fetch: true,
    cache_disable_url_params: ['override_hierarchy_restriction'],

    include_user_roles: true,
    current_location: null,
    form_collection: null, // if searching from a form, the form collection in question

    initialize: function (models, options) {
        MobileAccounts.__super__.initialize.apply(this, arguments);
        this.paginator.set('order_by', 'distance');

        if (this.form_collection && this.form_collection.metadata.get('unrestricted_locations')) {
            this.paginator.set('override_hierarchy_restriction', true);
            this.paginator.default_query_string_attrs.push('override_hierarchy_restriction');
        }
    },

    get_owned_accounts_for_active_user: function () {
        if (this.owned_accounts && this.owned_accounts.length) {
            return this.owned_accounts;
        }

        var accounts = [];
        this.forEach(function (account) {
            account.get('owners', []).forEach(function (owner) {
                if (owner.id == $f.active_user.id) {
                    accounts.push(account);
                }
            });
        });
        this.owned_accounts = accounts;
        return accounts;
    },

    /**
     * When we are getting results from our cache, we need to get them in the right order. This will a)
     * put the results in the correct order, and b) update each model.get('distance').
     */
    filter_cache_fetch: function (results_json) {
        if (this.paginator.get('order_by') != 'distance' || !this.loc) {
            // loc is set in fetch() below
            return results_json;
        }

        /**
         * Determine a distance in miles between two sets of lat/lon.
         */
        var get_distance_in_mi = function (lat1, lon1, lat2, lon2) {
            return ((Math.sqrt(Math.pow(lat1 - lat2, 2) + Math.pow(lon1 - lon2, 2)) * Math.PI) / 180) * 3960;
        };

        // Get all our vars in order
        var device_lat = this.loc.split(',')[0];
        var device_lon = this.loc.split(',')[1];

        return results_json.sort(function (first, second) {
            var first_lat = first.lat;
            var first_lon = first.lon;
            var second_lat = second.lat;
            var second_lon = second.lon;
            var first_dist = null;
            var second_dist = null;

            if (first_lat && first_lon) {
                first_dist = get_distance_in_mi(device_lat, device_lon, first_lat, first_lon);
                first.distance = first_dist;
            }

            if (second_lat && second_lon) {
                second_dist = get_distance_in_mi(device_lat, device_lon, second_lat, second_lon);
                second.distance = second_dist;
            }

            // Check for missing lat/lon's and prioritize appropriately
            if (first_dist && !second_dist) {
                return -1;
            } // if only 1st has loc, prioritize 1st
            if (second_dist && !first_dist) {
                return 1;
            } // if only 2nd has loc, prioritize 2nd
            if (!first_dist && !second_dist) {
                return 0;
            } // if neither have loc, they're equal

            // Since both have a lat/lon, make the final decision
            if (first_dist < second_dist) {
                return -1;
            } // 1st should come first
            if (second_dist < first_dist) {
                return 1;
            } // 2nd should come first
            return 0; // they're equal
        });
    },

    get_cache_key: function (options) {
        if (this.allow_cross_company_access) {
            return 'cross_company_accounts';
        }

        // Change our cache_key to whatever tag_id we're filtering by
        if (!this.tag_id || this.tag_id == $f.active_company.get('all_accounts_tag_id')) {
            // if tag is 'all accounts' tag, just use 'accounts' cache_key
            return 'accounts';
        } else {
            return 'accounts_for_tag_' + this.tag_id;
        }
    },

    /**
     * If we're ordering by distance, get our location before we make our fetch.
     */
    fetch: function () {
        var this_ = this;
        var arguments_ = arguments;

        this.cache_key = this.get_cache_key();

        if (this_.paginator.get('order_by') != 'distance') {
            this_.loc = null;
            return MobileAccounts.__super__.fetch.apply(this_, arguments_);
        }

        $f.mobile.get_device_location({
            success: function (position) {
                this_.loc = `${position.coords.lat},${position.coords.lon}`;
            },
            complete: function () {
                // no way to sort by distance if no lat/lon, so default to name
                if (!this_.loc) {
                    this_.paginator.set('order_by', 'name');
                }

                MobileAccounts.__super__.fetch.apply(this_, arguments_);
            }
        });
    }
});

export const LocationAttributes = BaseZenputModel.extend({
    defaults: {
        id: null,
        attributes: []
    },
    url: function () {
        return `/api/v3/locations/attributes/${this.id}/`;
    }
});

export const LocationsAttributes = BaseZenputCollection.extend(
    {
        model: LocationAttributes,
        paginator: null,
        cache_enabled: true,
        cache_key: 'locations_attributes',
        loc: null,
        initialize: function (models, options) {
            options = options || {};
            $.extend(this, options);
            if (!this.paginator) {
                this.paginator = new Paginator();
            }
        },
        fetch: function () {
            var this_ = this;
            var arguments_ = arguments;

            $f.mobile.get_device_location({
                success: function (position) {
                    this_.loc = `${position.coords.lat},${position.coords.lon}`;
                },
                complete: function () {
                    LocationsAttributes.__super__.fetch.apply(this_, arguments_);
                }
            });
        },
        url: function () {
            var endpoint = '/api/v3/locations/attributes/';
            if (this.loc) {
                return `${endpoint}?order_by=distance&loc=${this.loc}`;
            }
            return endpoint;
        }
    },
    {
        _fetch_and_add_to_cache: function (id, cache_items, success, error) {
            var class_ = this;
            var location_attributes = new LocationAttributes({ id: id });
            location_attributes.fetch({
                success: function (model) {
                    var new_cache_items = $f.utils.deepclone(cache_items);
                    new_cache_items.push(model.toJSON());
                    $f.mobile.cache.set(class_.prototype.cache_key, new_cache_items);
                    success(model);
                },
                error: error
            });
        },
        get_location_attributes: function (id) {
            return new Promise((resolve, reject) => {
                var class_ = this;
                var cache_get_success_callback = function (data) {
                    data = data || [];

                    var filtered = data.filter((item) => item.id !== undefined && item.id === id);

                    // if it's not on the cache, then fetch and add it
                    if (filtered.length === 0) {
                        return class_._fetch_and_add_to_cache(id, data, resolve, reject);
                    }
                    resolve(new LocationAttributes(filtered[0]));
                };
                $f.mobile.cache.get('locations_attributes', cache_get_success_callback, reject);
            });
        }
    }
);

export const AccountOrDistributeGroup = BaseZenputModel.extend({
    defaults: {
        id: null,
        name: null,
        type: null
    },

    constructor: function (attributes, options) {
        //
        // We have to transform the attributes so our id is unique
        // throughout the domain of all objects in the
        // AccountsAndDistributeGroups collection, both accounts and
        // distribute groups. We do this by combining 'type' and 'id'
        // to form a synthesized id.  We save the original id in
        // another attribute called 'raw_id'.
        //
        // We originally tried to fix this issue by defining the
        // 'model' member of AccountsAndDistributeGroups to be a
        // function that returns two possible model classes, one for
        // each type of object. This is a much more BackBone way of
        // doing things. But our custom mobile caching class does not
        // work properly when reconstituting objects created this way
        // and so this fix fails intermittently.
        //
        var transformed_attributes;
        if ('raw_id' in attributes) {
            // When we rebuild this object from data that's been cached, we are given the
            // attributes already transformed. So we don't run the transform again.
            transformed_attributes = attributes;
        } else {
            // Do the transform.
            transformed_attributes = $.extend({}, attributes);
            transformed_attributes.raw_id = attributes.id;
            transformed_attributes.id = `${attributes.type}-${attributes.id}`;
        }
        AccountOrDistributeGroup.__super__.constructor.call(this, transformed_attributes, options);
    }
});

export const AccountsForTaskBuckets = MobileAccounts.extend({
    cache_key: null, // set in initialize
    distribute_group_id: null,
    cache_merge_on_fetch: false,
    cache_fetch_limit: 25, // we'll only ever show limited account buckets because you'd be insane to scroll that far.

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        AccountsForTaskBuckets.__super__.initialize.apply(this, arguments);

        this.paginator.set({
            order_by: 'distance',
            group_id: this.distribute_group_id,
            limit: this.cache_fetch_limit
        });
    },

    get_cache_key: function () {
        var cache_key = AccountsForTaskBuckets.__super__.get_cache_key.apply(this, arguments);
        if (!this.distribute_group_id) {
            return cache_key;
        }
        return `${cache_key}_group_id_${this.distribute_group_id}`;
    }
});

export const AccountsAndDistributeGroups = BaseZenputCollection.extend({
    cache_enabled: true,
    cache_key: 'accounts_and_distribute_groups',

    paginator: null,

    model: AccountOrDistributeGroup,

    initialize: function (options) {
        options = options || {};
        $.extend(this, options);

        this.paginator = new Paginator({ order_by: 'name', limit: 10 });
    },

    url: function () {
        var attrs = this.paginator.default_query_string_attrs;
        if (!_.contains(attrs, 'search_query')) {
            attrs.push('search_query');
        }
        return '/api/v1/users/groups_and_accounts/?' + this.paginator.get_query_string();
    }
});

export const MobileUserRoles = UserRoles.extend({
    cache_enabled: true,
    cache_key: 'user_roles'
});

export const MobileBaseSearchOptions = Backbone.Collection.extend({
    cache_key: null,
    model: SearchOption,

    persist_on_change: true, // if true, will call save_local() whenever a change is triggered

    version: 1.5,

    /**
     * Version history:
     * v1.1: added project SearchOption
     * v1.2: refactor for mobile-only using NativeStorage
     */

    initialize: function (models, options) {
        options = options || {};
        $.extend(this, options);
        var this_ = this;

        this.initialize_options();

        if (!this.persist_on_change) {
            return;
        }
        this.on('change', function () {
            this_.save_local();
        });
    },

    initialize_options: function (models, options) {
        throw new Error('MobileBaseSearchOptions extension must implement initialize_options');
    },

    /**
     * Get the search options from NativeStorage
     * @return {object}        The JSON object from NativeStorage, or null
     */
    get_local: function (callback) {
        callback = callback || function () {};
        $f.mobile.settings.get(this.cache_key, function (val) {
            callback(val);
        });
    },

    /**
     * Loads any search options we have saved in localStorage/NativeStorage
     * @param  {Function} callback A callback to be fired when the fetch from local storage finishes
     */
    fetch_from_local: function (callback) {
        callback = callback || function () {};
        var this_ = this;

        this.get_local(function (local_save) {
            if (!local_save || !local_save.version || local_save.version != this_.version) {
                this_.clear_local(callback);
                return;
            }

            try {
                _.each(local_save.search_options, function (obj) {
                    var search_option = this_.findWhere({ field: obj.field });

                    var new_data = { field: obj.field, display_value: obj.display_value, value: obj.value };

                    if (!search_option) {
                        this_.add(new this_.model(new_data), { silent: true });
                    } else {
                        search_option.set(new_data, { silent: true });
                    }
                });
            } catch (err) {
                this_.clear_local(callback);
                return;
            }
            callback();
        });
    },

    clear_local: function (callback) {
        callback || (callback = function () {});
        $f.mobile.settings.set(this.cache_key, null, callback);
    },

    /**
     * Save the current search options to local storage
     */
    save_local: function (callback) {
        callback || (callback = function () {});
        $f.mobile.settings.set(this.cache_key, this.to_json_for_local_storage(), callback);
    },

    /**
     * toJSON() in the format that our local storage expects
     */
    to_json_for_local_storage: function () {
        return {
            version: this.version,
            search_options: this.map(function (so) {
                return { field: so.get('field'), value: so.get('value'), display_value: so.get('display_value') };
            })
        };
    },

    get_options_for_paginator: function () {
        return this.reduce(function (options, search_option) {
            if (!search_option.get('offline_only')) {
                options[search_option.get('field')] = search_option.get('value');
            }
            return options;
        }, {});
    },

    /**
     * Update a paginator with
     * @param Paginator: a paginator to update
     */
    update_paginator: function (paginator) {
        paginator.set(this.get_options_for_paginator());
    }
});

export const TaskProjectSearchOptions = MobileBaseSearchOptions.extend({
    set_valid_group_by_filter: function () {
        var group_by_option = this.findWhere({ field: 'group_by' });
        var account_option = this.findWhere({ field: 'account_id' });

        if (!group_by_option) {
            return;
        }

        if (account_option && account_option.get('value')) {
            this._set_group_by_location_visibility(false);
        } else {
            this._set_group_by_location_visibility(true);
        }

        this._reset_invalid_values();
    },

    get_value_for_field: function (field) {
        var model = this.findWhere({ field: field });

        if (!model) {
            if (field === 'status') {
                // Status field should never return null because bucket names depend on
                // the status.
                $f.mobile.logging.log_error('Error while retrieving search options field', {
                    field: field,
                    search_options: this.toJSON()
                });
                // since this is for a bugfixs and to get logged data we're just
                // hardcoding 'open' here, if it needs to be extended, we should make
                // a default_field_value: {status: 'open'} in the model and use that.
                return 'open';
            }
            return null;
        }

        return model.get('value');
    },

    _set_group_by_location_visibility: function (is_visible) {
        var select_options = this.findWhere({ field: 'group_by' }).get('select_options');
        var option = _.findWhere(select_options, { value: 'account' });
        option.is_visible = is_visible;
    },

    /**
     * Check all currently set values to make sure they're visible options. If they're not visible, reset
     * their value to the first that is visible. This will not update search options that have no select_options.
     */
    _reset_invalid_values: function () {
        this.each(function (search_option) {
            var select_options = search_option.get('select_options');
            var current_value = search_option.get('value');

            if (!select_options || !select_options.length) {
                return;
            }

            var current_select_option = _.findWhere(select_options, { value: current_value });

            if (current_select_option && current_select_option.is_visible !== false) {
                return;
            }

            var first_visible_option = _.findWhere(select_options, { is_visible: true });

            if (!first_visible_option) {
                search_option.set('value', null);
            } else {
                search_option.set('value', first_visible_option.value);
            }
        });
    }
});

/**
 * Search option collection for top level task list view
 */
export const TaskSearchOptions = TaskProjectSearchOptions.extend({
    cache_key: 'task_search_options',

    initialize_options: function () {
        let status_options;

        status_options = [
            {
                display_value: _t('mobile_common_all'),
                value: 'all'
            },
            {
                display_value: _t('mobile_tso_filter_incomplete'),
                value: 'open'
            },
            {
                display_value: _t('mobile_task_status_title_open'),
                value: 'open_available'
            },
            {
                display_value: _t('mobile_tso_filter_completed'),
                value: 'closed'
            },
            {
                display_value: _t('mobile_tso_filter_completed_late'),
                value: 'completed_late'
            },
            {
                display_value: _t('mobile_task_status_title_incomplete_and_closed'),
                value: 'archived_incomplete'
            }
        ];

        this.add([
            new SearchOption({
                field: 'filter',
                value: 'me',
                select_options: [
                    {
                        display_value: _t('mobile_tso_filter_all_tasks'),
                        value: 'all'
                    },
                    {
                        display_value: _t('mobile_tso_filter_my_tasks'),
                        value: 'me'
                    },
                    {
                        display_value: _t('mobile_common_created_by_me'),
                        value: 'by_me'
                    }
                ]
            }),
            new SearchOption({
                field: 'account_id',
                value: null
            }),
            new SearchOption({
                field: 'group_id',
                value: 'all'
            }),
            new SearchOption({
                field: 'status',
                value: 'open',
                select_options: status_options
            }),
            new SearchOption({
                field: 'order_by',
                value: 'date_due',
                offline_only: false,
                select_options: [
                    {
                        display_value: _t('mobile_tasks_filter_due_time'),
                        value: 'date_due_local',
                        is_visible: true
                    },
                    {
                        display_value: _t('mobile_tasks_filter_open_time'),
                        value: 'date_start_local',
                        is_visible: true
                    }
                ]
            }),
            new SearchOption({
                field: 'group_by',
                value: 'default',
                offline_only: true,
                select_options: [
                    {
                        display_value: _t('mobile_tso_filter_none'),
                        value: 'default',
                        is_visible: true
                    },
                    {
                        display_value: _t('mobile_common_location'),
                        value: 'account',
                        is_visible: true
                    }
                ]
            }),
            new SearchOption({
                field: 'search_query',
                value: null
            })
        ]);
    },

    /**
     * Get an English representation of the current search options.
     */
    get_filter_string: function () {
        var this_ = this;
        var phrase = '';
        var filter = this.get_value_for_field('filter');
        var status = this.get_value_for_field('status');
        var group_by = this.get_value_for_field('group_by') || '';
        var group_id = this.get_value_for_field('group_id');
        var account_id = this.get_value_for_field('account_id');
        var project_id = this.get_value_for_field('project_id');
        var search_query = this.get_value_for_field('search_query');

        var status_strings = {
            closed: 'mobile_tso_filter_completed',
            archived_incomplete: 'mobile_task_status_title_incomplete_and_closed',
            open: 'mobile_tso_filter_incomplete',
            open_available: 'mobile_task_status_title_open',
            completed_late: 'mobile_tso_filter_completed_late'
        };

        var status_word = status !== 'all' ? _t(status_strings[status]) : '';

        // filter and status
        if (filter === 'me') {
            phrase += _t('mobile_tso_filter_var_my_tasks', { filter_type: status_word });
        } else if (filter === 'by_me') {
            phrase += _t('mobile_tso_filter_var_tasks_created_by_me', { filter_type: status_word });
        } else {
            phrase += _t('mobile_tso_filter_var_all_tasks', { filter_type: status_word });
        }

        // team
        if (filter !== 'me' && group_id) {
            // we don't filter by account/team if only showing my tasks
            var group_filter = this_.findWhere({ field: 'group_id' });
            if (group_filter.get('value') !== 'all' && group_filter.get('display_value')) {
                phrase += _t('mobile_common_filter_var_in', { team_or_location: group_filter.get('display_value') });
            }
        }

        // account
        if (account_id) {
            phrase += _t('mobile_tso_filter_var_at', {
                location: this_.findWhere({ field: 'account_id' }).get('display_value')
            });
        }

        if (project_id) {
            // then this is being used for the project task search options (@see ProjectTaskSearchOptions)
            phrase += _t('mobile_common_filter_var_in', {
                team_or_location: this_.findWhere({ field: 'project_id' }).get('display_value')
            });
        }

        if (status !== 'closed' && status !== 'archived_incomplete') {
            const order_by = this.get_value_for_field('order_by');
            const group_by_strings = {
                date_due_local: _t('mobile_tasks_filter_due_time'),
                date_start_local: _t('mobile_tasks_filter_open_time')
            };
            phrase += `, ${_t('mobile_tasks_filter_sorted_by')} ${group_by_strings[order_by]}`;
        }

        if (group_by === 'account' && !search_query) {
            phrase += _t('mobile_tso_filter_group_distance');
        }

        return phrase;
    }
});

/**
 * Search option collection for dashboard task tab
 *
 * Different from the TaskSearchOptions to have different filters for dashboard vs task page
 */
export const DashboardTaskSearchOptions = TaskSearchOptions.extend({
    cache_key: 'dashboard_task_search_options'
});

/**
 * We are using a new set of search options here for projects filtered by tasks.
 * This avoids problems with having to reset project_id on task views depending on
 * which path your reach the task list from.
 */
export const ProjectTaskSearchOptions = TaskSearchOptions.extend({
    cache_key: 'project_task_search_options',

    initialize_options: function () {
        ProjectTaskSearchOptions.__super__.initialize_options.apply(this, arguments);

        this.add([
            new SearchOption({
                field: 'project_ids',
                value: null
            }),
            new SearchOption({
                field: 'is_active',
                value: true
            })
        ]);

        // We don't show Open tasks in Project's task list. The filter results in an empty page since there is no bucket for it
        const status_filter_option = this.find((item) => {
            return item.get('field') === 'status';
        });
        const status_options = status_filter_option.get('select_options');

        const filtered_status_options = status_options.filter((item) => {
            return item.value !== 'open_available';
        });
        status_filter_option.set({ select_options: filtered_status_options });
    }
});

export const ProjectSearchOptions = TaskProjectSearchOptions.extend({
    cache_key: 'project_search_options',

    initialize_options: function () {
        this.add([
            new SearchOption({
                field: 'filter',
                value: 'all',
                select_options: [
                    {
                        display_value: _t('mobile_pso_filter_all_projects'),
                        value: 'all'
                    },
                    {
                        display_value: _t('mobile_common_created_by_me'),
                        value: 'by_me'
                    }
                ]
            }),
            new SearchOption({
                field: 'type',
                value: 'all',
                select_options: [
                    {
                        display_value: _t('mobile_pso_filter_all_projects'),
                        value: 'all'
                    },
                    {
                        display_value: _t('mobile_pso_filter_recurring'),
                        value: 'recurring'
                    },
                    {
                        display_value: _t('mobile_pso_filter_non_recurring'),
                        value: 'non_recurring'
                    }
                ]
            }),
            new SearchOption({
                field: 'group_id',
                value: $f.mobile.get_active_user_distribute_group().id,
                display_value: $f.mobile.get_active_user_distribute_group().display_value
            }),
            new SearchOption({
                field: 'is_active',
                value: true,
                select_options: [
                    {
                        display_value: _t('mobile_common_all'),
                        value: ''
                    },
                    {
                        display_value: _t('mobile_pso_filter_active'),
                        value: true
                    },
                    {
                        display_value: _t('mobile_pso_filter_archived'),
                        value: false
                    }
                ]
            }),
            new SearchOption({
                field: 'search_query',
                value: null
            })
        ]);
    },

    /**
     * Get an English representation of the current search options.
     */
    get_filter_string: function () {
        var this_ = this;
        var phrase = '';
        var filter = this_.findWhere({ field: 'filter' }).get('value');
        var is_active = this_.findWhere({ field: 'is_active' }).get('value');
        var is_active_word = '';
        if (typeof is_active === 'boolean') {
            is_active_word = _t(is_active ? 'mobile_pso_filter_active' : 'mobile_pso_filter_archived');
        }
        var type = this_.findWhere({ field: 'type' }).get('value');

        // type = 'recurring' or 'non_recurring'
        var type_word = type === 'all' ? '' : _t('mobile_pso_filter_' + type);
        // filter and is_active
        if (filter === 'me') {
            phrase += _t('mobile_pso_filter_var_my_projects', {
                project_type: type_word,
                active_description: is_active_word
            });
        } else if (filter === 'by_me') {
            phrase += _t('mobile_pso_filter_var_my_created_projects', {
                project_type: type_word,
                active_description: is_active_word
            });
        } else {
            phrase += _t('mobile_pso_filter_var_all_projects', {
                project_type: type_word,
                active_description: is_active_word
            });
        }

        // team
        if (filter !== 'me') {
            // we don't filter by account/team if only showing my tasks
            var group_filter = this_.findWhere({ field: 'group_id' });
            if (group_filter.get('value') && group_filter.get('value') !== 'all') {
                phrase += _t('mobile_common_filter_var_in', { team_or_location: group_filter.get('display_value') });
            }
        }

        phrase += _t('mobile_tso_filter_group_date_due');

        return phrase;
    }
});

/**
 * Model representing a single tab of a our tabbed nav.
 * @see TabbedNav
 */
export const TabbedLayoutPageListItem = BaseZenputModel.extend({
    defaults: {
        id: null,
        tab_class: '',
        name: '',
        view_class: null,
        view_options: null
    }
});

export const TabbedLayoutPageList = BaseZenputCollection.extend({
    model: TabbedLayoutPageListItem,
    active_id: null // the active tab model ID, if any
});

export const UserUnreadCount = BaseZenputModel.extend({
    default: {
        count: 0
    },
    url: '/api/v2/comments/user_unread_count/'
});
